import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import {User, Wallet, Save, LogOut, ArrowLeft, Sparkles} from 'lucide-react'

function Profile() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut } = useAuth()
  const [solanaAddress, setSolanaAddress] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [totalMoons, setTotalMoons] = useState(0)

  useEffect(() => {
    if (user?.userId) {
      loadPlayerData()
    }
  }, [user])

  // Refresh on navigation (when component mounts or remounts)
  useEffect(() => {
    if (user?.userId) {
      const timeoutId = setTimeout(() => {
        loadPlayerData()
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [location.pathname, user?.userId])

  // Refresh data when page becomes visible or window gains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user?.userId) {
        loadPlayerData()
      }
    }

    const handleFocus = () => {
      if (user?.userId) {
        loadPlayerData()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [user])

  const loadPlayerData = async () => {
    setLoading(true)
    try {
      // Force cache bypass with timestamp
      const timestamp = Date.now()
      console.log(`[Profile ${timestamp}] 🔄 Loading player data for userId:`, user?.userId)
      
      // Load player info
      const { list } = await lumi.entities.players.list({
        filter: { userId: user.userId }
      })
      if (list.length > 0) {
        setSolanaAddress(list[0].solanaAddress || '')
      }

      // Load lifetime moons collected - fetch ALL scores, no limit
      const { list: scores } = await lumi.entities.scores.list({
        filter: { userId: user.userId },
        sort: { createdAt: -1 },
        limit: 10000
      })
      console.log(`[Profile ${timestamp}] 📊 Loaded ${scores.length} scores for user`)
      console.log(`[Profile ${timestamp}] 📋 Scores:`, scores.map(s => ({ score: s.score, date: s.createdAt })))
      
      const lifetimeMoons = scores.reduce((sum, score) => sum + (Number(score.score) || 0), 0)
      console.log(`[Profile ${timestamp}] 🌙 Total lifetime moons calculated:`, lifetimeMoons)
      setTotalMoons(lifetimeMoons)
    } catch (error) {
      console.error('[Profile] ❌ Failed to load player data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSolanaAddress = async () => {
    if (!solanaAddress.trim()) {
      setMessage('Please enter a valid Solana address')
      return
    }

    setSaving(true)
    setMessage('')
    try {
      const { list } = await lumi.entities.players.list({
        filter: { userId: user.userId }
      })

      const now = new Date().toISOString()

      if (list.length > 0) {
        await lumi.entities.players.update(list[0]._id, {
          solanaAddress: solanaAddress.trim(),
          updatedAt: now
        })
      } else {
        await lumi.entities.players.create({
          userId: user.userId,
          username: user.userName || user.email.split('@')[0],
          solanaAddress: solanaAddress.trim(),
          createdAt: now,
          updatedAt: now
        })
      }
      setMessage('✅ Solana address saved successfully!')
    } catch (error: any) {
      setMessage('❌ Failed to save: ' + (error.message || 'Unknown error'))
      console.error('Save failed:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleSignOut = () => {
    signOut()
    navigate('/login')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0118] via-[#0f0520] to-[#1a0a2e] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent mb-4"></div>
          <div className="text-white text-xl font-semibold">Loading profile...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0118] via-[#0f0520] to-[#1a0a2e] p-4 overflow-hidden relative">
      {/* Animated stars */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(150)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full animate-pulse"
            style={{
              width: Math.random() * 3 + 0.5 + 'px',
              height: Math.random() * 3 + 0.5 + 'px',
              left: Math.random() * 100 + '%',
              top: Math.random() * 100 + '%',
              animationDelay: Math.random() * 3 + 's',
              animationDuration: Math.random() * 2 + 2 + 's',
              opacity: Math.random() * 0.7 + 0.3,
              boxShadow: `0 0 ${Math.random() * 4 + 2}px rgba(255,255,255,${Math.random() * 0.6})`
            }}
          />
        ))}
      </div>

      {/* Nebula effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
        <div className="absolute w-96 h-96 rounded-full blur-3xl bg-purple-500/40 left-10 top-20 animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute w-80 h-80 rounded-full blur-3xl bg-blue-500/30 right-20 bottom-20 animate-pulse" style={{ animationDuration: '10s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto pt-8">
        {/* Header */}
        <button
          onClick={() => navigate('/game')}
          className="mb-6 flex items-center gap-2 text-gray-400 hover:text-white transition-all duration-300 hover:gap-3 group"
        >
          <ArrowLeft size={20} className="group-hover:animate-pulse" />
          <span className="font-semibold">Back to Game</span>
        </button>

        <div className="bg-black/70 backdrop-blur-xl p-8 rounded-2xl border-2 border-purple-500/50 shadow-[0_0_60px_rgba(168,85,247,0.3)]">
          {/* Profile Header */}
          <div className="flex items-center gap-6 mb-8 pb-6 border-b-2 border-purple-500/30">
            <div className="relative">
              <div className="absolute inset-0 blur-xl bg-red-500/50 rounded-full animate-pulse" />
              <div className="relative w-20 h-20 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center border-4 border-white/50 shadow-xl">
                <User size={40} className="text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-black text-white mb-1" style={{
                textShadow: '0 0 20px rgba(255,255,255,0.5)'
              }}>
                {user?.userName || 'Space Hopper'}
              </h1>
              <p className="text-gray-400 font-semibold">{user?.email}</p>
            </div>
          </div>

          {/* Lifetime Stats */}
          <div className="mb-8 p-6 bg-gradient-to-r from-yellow-500/20 via-yellow-600/10 to-yellow-500/20 border-2 border-yellow-400/50 rounded-xl backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-4xl">🌙</div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Lifetime Moons Collected</h3>
                  <p className="text-4xl font-black text-white mt-1" style={{
                    textShadow: '0 0 20px rgba(251,191,36,0.8)'
                  }}>
                    {totalMoons.toLocaleString()}
                  </p>
                </div>
              </div>
              <Sparkles size={40} className="text-yellow-400 animate-pulse" />
            </div>
          </div>

          {/* Solana Address Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <Wallet size={28} className="text-yellow-400" />
              <h2 className="text-2xl font-black text-white">Solana Address</h2>
              <Sparkles size={20} className="text-yellow-400 animate-pulse" />
            </div>

            <div className="p-4 bg-yellow-500/10 border-2 border-yellow-500/30 rounded-xl backdrop-blur-sm">
              <p className="text-gray-300 text-sm font-semibold">
                💎 Add your Solana wallet address to be eligible for leaderboard airdrops! 
                Only the address is needed (not wallet connection).
              </p>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                value={solanaAddress}
                onChange={(e) => setSolanaAddress(e.target.value)}
                placeholder="Enter your Solana address (e.g., 7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU)"
                className="w-full px-4 py-3 bg-black/50 border-2 border-purple-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-all duration-300 font-mono text-sm backdrop-blur-sm"
              />

              <button
                onClick={handleSaveSolanaAddress}
                disabled={saving}
                className="w-full py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 disabled:from-gray-700 disabled:to-gray-800 text-black disabled:text-gray-400 font-black text-lg rounded-xl transition-all duration-300 shadow-[0_0_30px_rgba(234,179,8,0.4)] hover:shadow-[0_0_50px_rgba(234,179,8,0.7)] disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2 border-2 border-yellow-400 disabled:border-gray-600"
              >
                <Save size={20} />
                {saving ? 'Saving...' : 'Save Address'}
              </button>

              {message && (
                <div className={`p-4 rounded-xl text-sm font-semibold backdrop-blur-sm border-2 ${
                  message.startsWith('✅') 
                    ? 'bg-green-500/20 border-green-500 text-green-200' 
                    : 'bg-red-500/20 border-red-500 text-red-200'
                }`}>
                  {message}
                </div>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="my-8 border-t-2 border-purple-500/30"></div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 bg-transparent hover:bg-yellow-500/20 border-2 border-yellow-500 text-yellow-400 font-bold rounded-xl transition-all duration-300 hover:shadow-[0_0_30px_rgba(234,179,8,0.3)] text-lg"
            >
              Menu
            </button>

            <button
              onClick={handleSignOut}
              className="w-full py-3 bg-transparent hover:bg-red-600/20 border-2 border-red-600 text-red-500 font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 hover:shadow-[0_0_30px_rgba(220,38,38,0.3)] text-lg"
            >
              <LogOut size={20} />
              Sign Out
            </button>
          </div>
        </div>
      </div>

    </div>
  )
}

export default Profile
